import { useAskInputActions, useAskInputState, } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { getQueryWithoutSourceMentions } from '#pplx/frontend/apps/ask/ask-input/query-serialization/lexicalNodeToQuery.js';
import { createAskAutosuggestPlugin } from '#pplx/frontend/apps/ask/composer-sdk-plugins/autosuggest/createAskAutosuggestPlugin.js';
import { createSuggestionRequests } from '#pplx/frontend/apps/ask/composer-sdk-plugins/autosuggest/createSuggestionRequests.js';
import { resolveAutosuggestQuery } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/resolveAutosuggestQuery.js';
import { resolveSuggestionsDisabled } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/resolveSuggestionsDisabled.js';
import { useAskInputAutosuggestActions } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAskInputAutosuggestActions.js';
import { useAskInputSubmitWithMentions } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAskInputSubmitWithMentions.js';
import { useAskInputSuggestionPolicySnapshot } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAskInputSuggestionPolicy.js';
import { useAskInputSuggestionSubmission } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAskInputSuggestionSubmission.js';
import { useAskInputSuggestionView } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAskInputSuggestionView.js';
import { useAutosuggest } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useAutosuggest.js';
import { useBlankStateSuggest } from '#pplx/frontend/apps/ask/suggest-dropdown/hooks/useBlankStateSuggest.js';
import { useFillSuggestion } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/hooks/useFillSuggestion.js';
import { useSuggestionRowIcon } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/hooks/useSuggestionRowIcon.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { createPlainTextComposerValue } from '#pplx/frontend/composer-sdk/createPlainTextComposerValue.js';
import { serializedStateToMarkdown } from '#pplx/frontend/composer-sdk/serializedStateToMarkdown.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
/** Adapts shared Ask suggestions to Composer's provider and synchronous draft callbacks. */
export function useComposerAutosuggestControls(options) {
    const { autosuggestionsEnabled = true, fillInput, forceHideSuggestions = false, inputJson, inputQuery, isTranscribing, querySource, selectedSearchMode, } = options;
    const { activeMenu, blankStateSuggestions, suggestions } = useAskInputState();
    const { setSuggestions } = useAskInputActions();
    const { policy, suggestionsDisabled } = useAskInputSuggestionPolicySnapshot({
        autosuggestionsEnabled,
        hasThreadContent: options.hasThreadContent ?? false,
        inputQuery,
        querySource,
        suggestions,
    });
    const draftRef = useRef({ inputJson, inputQuery });
    draftRef.current = { inputJson, inputQuery };
    /**
     * Restored or externally replaced drafts do not emit onComposerValueChange;
     * keep suggestions quiet until the user changes the text.
     */
    const hasUserEditedRef = useRef(false);
    const policyRef = useRef(policy);
    policyRef.current = policy;
    const suggestionsRef = useRef(suggestions);
    suggestionsRef.current = suggestions;
    const [requests] = useState(() => createSuggestionRequests({
        onResults: (nextSuggestions) => {
            suggestionsRef.current = nextSuggestions;
        },
    }));
    const { getCachedSuggestions, handleUpdateAutosuggestions } = useAutosuggest({
        enabled: autosuggestionsEnabled && !policy.isSuggestionsDisabled,
        onSuggestionsResolved: requests.settle,
    });
    const updateRef = useRef(handleUpdateAutosuggestions);
    updateRef.current = handleUpdateAutosuggestions;
    const updateAutosuggestions = useCallback((query) => {
        requests.invalidate();
        updateRef.current(query);
    }, [requests]);
    const publishSuggestions = useCallback((nextSuggestions) => {
        suggestionsRef.current = nextSuggestions;
        setSuggestions(nextSuggestions);
    }, [setSuggestions]);
    useEffect(() => () => requests.clear(), [requests]);
    const { updateSuggestions, showBlankSuggestions, resetSuggestions } = useAskInputAutosuggestActions({
        blankStateSuggestions,
        handleUpdateAutosuggestions: updateAutosuggestions,
        selectedSearchMode,
        setSuggestions: publishSuggestions,
        suggestionsDisabled,
    });
    const [activateOnEmptyQuery, setActivateOnEmptyQuery] = useState(false);
    const resetAutosuggestions = useCallback(() => {
        hasUserEditedRef.current = false;
        requests.clear();
        resetSuggestions();
        setActivateOnEmptyQuery(false);
    }, [requests, resetSuggestions]);
    useEffect(() => {
        if (suggestionsDisabled)
            resetAutosuggestions();
    }, [resetAutosuggestions, suggestionsDisabled]);
    const onComposerClick = useCallback(() => {
        if (draftRef.current.inputQuery.length > 0)
            return;
        showBlankSuggestions(draftRef.current.inputQuery);
        setActivateOnEmptyQuery(true);
    }, [showBlankSuggestions]);
    const onComposerValueChange = useCallback((nextValue) => {
        const previousInputQuery = draftRef.current.inputQuery;
        const nextInputQuery = serializedStateToMarkdown(nextValue);
        draftRef.current = { inputJson: nextValue, inputQuery: nextInputQuery };
        if (nextInputQuery === previousInputQuery)
            return;
        hasUserEditedRef.current = true;
        updateSuggestions(nextInputQuery, nextValue);
        setActivateOnEmptyQuery(true);
    }, [updateSuggestions]);
    const resolveRequest = useCallback(() => {
        const draft = draftRef.current;
        if (resolveSuggestionsDisabled({
            ...policyRef.current,
            inputQuery: draft.inputQuery,
            suggestions: suggestionsRef.current,
        }) ||
            draft.inputQuery.includes('\n') ||
            /^\s*[/@]\s*$/u.test(draft.inputQuery)) {
            return { kind: 'ignored' };
        }
        return resolveAutosuggestQuery({
            inputQuery: draft.inputQuery,
            queryWithoutSourceMentions: getQueryWithoutSourceMentions(draft.inputJson?.root, draft.inputQuery),
        });
    }, []);
    const getPendingSuggestions = useCallback(() => {
        const request = resolveRequest();
        switch (request.kind) {
            case 'ignored':
            case 'source-only':
                return EMPTY_ARRAY;
            case 'blank':
                return suggestionsRef.current;
            case 'query': {
                if (!hasUserEditedRef.current)
                    return EMPTY_ARRAY;
                const cached = getCachedSuggestions(request.query);
                return cached.length > 0 ? cached : suggestionsRef.current;
            }
            default:
                return assertUnreachableValue(request);
        }
    }, [getCachedSuggestions, resolveRequest]);
    const getSuggestions = useCallback(({ abortSignal, }) => {
        const request = resolveRequest();
        switch (request.kind) {
            case 'ignored':
            case 'source-only':
                return EMPTY_ARRAY;
            case 'blank':
                return suggestionsRef.current;
            case 'query':
                if (!hasUserEditedRef.current)
                    return EMPTY_ARRAY;
                return abortSignal.aborted
                    ? EMPTY_ARRAY
                    : requests.wait({ query: request.query, abortSignal });
            default:
                return assertUnreachableValue(request);
        }
    }, [requests, resolveRequest]);
    const isQueryEligible = useCallback(() => {
        if (activeMenu !== null ||
            !autosuggestionsEnabled ||
            forceHideSuggestions ||
            policy.isSuggestionsDisabled ||
            isTranscribing) {
            return false;
        }
        const request = resolveRequest();
        return request.kind === 'blank' || (request.kind === 'query' && hasUserEditedRef.current);
    }, [
        activeMenu,
        autosuggestionsEnabled,
        forceHideSuggestions,
        policy.isSuggestionsDisabled,
        isTranscribing,
        resolveRequest,
    ]);
    const handleSubmitWithMentions = useAskInputSubmitWithMentions({
        json: inputJson,
        handleSubmit: options.handleSubmit,
    });
    const handleFillInput = useCallback((filledText) => {
        // Picking a suggestion is a user gesture, so refresh suggestions for the
        // filled text just as the legacy input does.
        const filledValue = createPlainTextComposerValue(filledText);
        draftRef.current = { inputJson: filledValue, inputQuery: filledText };
        hasUserEditedRef.current = true;
        updateSuggestions(filledText, filledValue);
        fillInput(filledText);
    }, [fillInput, updateSuggestions]);
    const handleSuggestionSubmission = useAskInputSuggestionSubmission({
        fillInput: handleFillInput,
        handleSubmit: handleSubmitWithMentions,
        inputQueryForAnalytics: inputQuery,
        searchModeOverride: selectedSearchMode,
        querySource,
    });
    const handleSuggestionView = useAskInputSuggestionView({ inputQuery, querySource });
    const getRowIcon = useSuggestionRowIcon();
    const handleFill = useFillSuggestion(handleFillInput);
    useBlankStateSuggest({
        attachments: options.attachments,
        isCometHome: options.isCometHome ?? false,
        query: inputQuery,
        querySource,
        reason: `ask-input-inner-${querySource}`,
        selectedSearchMode,
        sources: options.sources,
        suggestionsDisabled,
    });
    const enabled = isQueryEligible();
    const autosuggestPlugin = useMemo(() => createAskAutosuggestPlugin({
        enabled,
        // Empty-input activation bypasses matchQuery, so it needs the same display gates.
        activateOnEmptyQuery: activateOnEmptyQuery && suggestions.length > 0 && enabled,
        getPendingSuggestions,
        getRowIcon,
        getSuggestions,
        isQueryEligible,
        onSelect: handleSuggestionSubmission,
        onFill: handleFill,
        onSuggestionsView: handleSuggestionView,
    }), [
        activateOnEmptyQuery,
        enabled,
        getPendingSuggestions,
        getRowIcon,
        getSuggestions,
        handleSuggestionSubmission,
        handleFill,
        handleSuggestionView,
        isQueryEligible,
        suggestions.length,
    ]);
    const plugins = useMemo(() => [autosuggestPlugin], [autosuggestPlugin]);
    return useMemo(() => ({ onComposerClick, onComposerValueChange, plugins, resetAutosuggestions }), [onComposerClick, onComposerValueChange, plugins, resetAutosuggestions]);
}
//# sourceMappingURL=useComposerAutosuggestControls.js.map